import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Mail, Shield, RefreshCw, CheckCircle } from "lucide-react";
import { ErrorCallout } from "@/components/ui/error-callout";
import type { IntakeData } from "@/lib/types";
import { patientIntakeApi } from "@/lib/patientIntakeApi";
import { useLingui } from "@lingui/react";
import { msg } from "@lingui/macro";

interface OTPVerificationStepProps {
  data: IntakeData;
  onUpdate: (data: Partial<IntakeData>) => void;
  onVerificationSuccess: () => void;
  onBack?: () => void;
  formUid?: string;
  referenceUid?: string;
}

type AuthMode = "magic-link" | "otp";

export default function OTPVerificationStep({
  data,
  onBack,
  formUid,
  referenceUid,
}: OTPVerificationStepProps) {
  const { _ } = useLingui();
  const [authMode, setAuthMode] = useState<AuthMode>("magic-link");
  const [otp, setOtp] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState("");
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [timeLeft, setTimeLeft] = useState(0);
  const [challengeId, setChallengeId] = useState("");
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [lastMagicLinkSentAt, setLastMagicLinkSentAt] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const authInitiatedRef = useRef(false);

  useEffect(() => {
    // Auto-send magic link when component mounts (only once)
    if (!authInitiatedRef.current && formUid && referenceUid) {
      authInitiatedRef.current = true;
      sendMagicLink();
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount, not when formUid/referenceUid change

  useEffect(() => {
    if (timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [timeLeft]);

  const sendMagicLink = async (options?: { forceResend?: boolean }) => {
    if (!formUid || !referenceUid) {
      setError(_(msg`Unable to send magic link. Missing intake reference.`));
      setErrorStatus(400);
      return;
    }

    setIsSending(true);
    setError("");
    setErrorStatus(null);
    setSuccessMessage("");

    try {
      const result = await patientIntakeApi.startAuth({
        email: data.email,
        formUid,
        referenceUid,
        forceResend: options?.forceResend ?? false,
      });

      setMagicLinkSent(true);
      setLastMagicLinkSentAt(result.data?.sentAt ?? new Date().toISOString());
      
      if (result.data?.alreadySent) {
        setSuccessMessage(
          _(msg`A verification link was just sent to your email. Please check your inbox (and spam folder) before requesting another.`)
        );
      } else if (options?.forceResend) {
        setSuccessMessage(
          _(msg`We've resent the verification link to your email. Please check your inbox (including spam folder) and click the link to continue.`)
        );
      } else if (result.data?.requiresOtpFallback) {
        setSuccessMessage(
          _(msg`We've sent a verification link to your email. Click the link to continue, or use the code option below if you prefer.`)
        );
      } else {
        setSuccessMessage(
          _(msg`We've sent a verification link to your email. Please check your inbox (including spam folder) and click the link to continue.`)
        );
      }
      
      setAuthMode("magic-link");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : _(msg`Failed to send magic link`);
      const errorStatus =
        err instanceof Error ? Number((err as { status?: number }).status) : 0;
      
      // Surface backend error directly. For forbidden role errors, do not
      // auto-fallback because OTP will be blocked for the same account.
      console.warn("Magic link send failed:", errorMessage);
      setError(errorMessage);
      setErrorStatus(errorStatus || null);
      if (errorStatus === 403) {
        setAuthMode("magic-link");
        return;
      }

      setAuthMode("otp");
      
      // Automatically try OTP
      setTimeout(() => {
        void sendOTP();
      }, 1000);
    } finally {
      setIsSending(false);
    }
  };

  const sendOTP = async () => {
    if (!formUid) {
      setError(_(msg`Unable to send verification code. Missing intake reference.`));
      setErrorStatus(400);
      return;
    }

    setIsSending(true);
    setError("");
    setErrorStatus(null);
    setSuccessMessage("");
    setOtp("");
    setRemainingAttempts(null);

    try {
      const result = await patientIntakeApi.sendVerificationCode(data.email, formUid);
      setChallengeId(result.data?.challengeId || "");
      setTimeLeft(15 * 60); // 15 minutes
      setSuccessMessage(_(msg`Verification code sent successfully! Check your email (including spam folder).`));
      setAuthMode("otp");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : _(msg`Network error. Please check your connection.`);
      const errorStatus =
        err instanceof Error ? Number((err as { status?: number }).status) : 0;
      
      // Parse remaining time from backend error message
      const minutesMatch = errorMessage.match(/(\d+)\s+minutes?/);
      const secondsMatch = errorMessage.match(/(\d+)\s+seconds?/);
      
      if (minutesMatch) {
        const minutes = parseInt(minutesMatch[1]);
        setTimeLeft(minutes * 60);
      } else if (secondsMatch) {
        const seconds = parseInt(secondsMatch[1]);
        setTimeLeft(seconds);
      } else if (errorMessage.includes("few seconds")) {
        setTimeLeft(30);
      } else {
        setTimeLeft(0);
      }
      
      setError(errorMessage);
      setErrorStatus(errorStatus || null);
    } finally {
      setIsSending(false);
    }
  };

  const verifyOTP = async () => {
    if (!formUid) {
      setError(_(msg`Unable to verify code. Missing intake reference.`));
      setErrorStatus(400);
      return;
    }

    if (otp.length !== 6) {
      setError(_(msg`Please enter a 6-digit verification code`));
      setErrorStatus(null);
      setRemainingAttempts(null);
      return;
    }

    setIsVerifying(true);
    setError("");
    setErrorStatus(null);
    setSuccessMessage("");

    try {
      const result = await patientIntakeApi.verifyEmail(
        data.email,
        otp,
        formUid,
        challengeId,
      );

      // Store tokens if provided
      if (result.data?.accessToken) {
        localStorage.setItem("accessToken", result.data.accessToken);
        if (result.data.refreshToken) {
          localStorage.setItem("refreshToken", result.data.refreshToken);
        }
      }

      // Show success message before redirect
      setSuccessMessage(_(msg`Email verified successfully! Redirecting to your dashboard...`));

      // Redirect to jema-manager dashboard after a brief delay
      setTimeout(() => {
        const jemaManagerUrl =
          import.meta.env.VITE_JEMA_MANAGER_URL || "http://localhost:5173";
        window.location.href = jemaManagerUrl;
      }, 1500);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : _(msg`Network error. Please try again.`);
      const errorStatus =
        err instanceof Error ? Number((err as { status?: number }).status) : 0;
      
      // Parse remaining attempts from error message
      const attemptsMatch = errorMessage.match(/You have (\d+) attempt/);
      if (attemptsMatch) {
        setRemainingAttempts(parseInt(attemptsMatch[1]));
      } else {
        setRemainingAttempts(null);
      }
      
      // Handle specific error cases
      if (errorMessage.includes("expired") || errorMessage.includes("Verification code has expired")) {
        setError(errorMessage);
        setErrorStatus(errorStatus || null);
        setTimeLeft(0);
      } else if (errorMessage.includes("Maximum verification attempts exceeded")) {
        setError(errorMessage);
        setErrorStatus(errorStatus || null);
        setTimeLeft(0);
        setOtp("");
      } else {
        setError(errorMessage);
        setErrorStatus(errorStatus || null);
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleOtpChange = (value: string) => {
    const numericValue = value.replace(/\D/g, "").slice(0, 6);
    setOtp(numericValue);
    setError("");
    setErrorStatus(null);
    setSuccessMessage("");
    setRemainingAttempts(null);
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const switchToOTP = () => {
    setAuthMode("otp");
    setError("");
    setErrorStatus(null);
    setSuccessMessage("");
    if (!challengeId) {
      void sendOTP();
    }
  };

  return (
    <div className="max-w-md mx-auto space-y-6">
      <div className="text-center">
        <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
          <Shield className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          {_(msg`Verify Your Email`)}
        </h2>
        <p className="text-gray-600">
          {authMode === "magic-link" 
            ? _(msg`We've sent a verification link to`)
            : _(msg`We've sent a 6-digit verification code to`)
          }
        </p>
        <p className="font-medium text-gray-900">{data.email}</p>
      </div>

      {authMode === "magic-link" ? (
        // Magic Link Mode
        <Card>
          <CardContent className="pt-6 space-y-4">
            {isSending ? (
              <div className="text-center py-8">
                <RefreshCw className="w-8 h-8 mx-auto mb-4 animate-spin text-blue-600" />
                <p className="text-gray-600">{_(msg`Sending verification link...`)}</p>
              </div>
            ) : (
              <>
                {successMessage && !error && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <div className="flex items-start space-x-3">
                      <Mail className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="space-y-2 flex-1">
                        <p className="text-blue-800 text-sm font-medium">
                          {successMessage}
                        </p>
                        <p className="text-blue-700 text-xs">
                          {_(msg`The link will expire in 1 hour. If you don't see it, check your spam folder.`)}
                        </p>
                        {lastMagicLinkSentAt && (
                          <p className="text-blue-700 text-xs">
                            {_(msg`Last sent at`)} {new Date(lastMagicLinkSentAt).toLocaleTimeString([], {
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {error && (
                  <ErrorCallout message={error} status={errorStatus} />
                )}

                <div className="text-center py-4">
                  <p className="text-sm text-gray-600 mb-4">
                    {_(msg`Prefer a verification code instead?`)}
                  </p>
                  <Button
                    onClick={switchToOTP}
                    variant="outline"
                    className="w-full"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {_(msg`Use Verification Code`)}
                  </Button>
                </div>

                {magicLinkSent && (
                  <div className="text-center pt-4 border-t">
                    <p className="text-xs text-gray-500 mb-2">{_(msg`Didn't receive the link?`)}</p>
                    <Button
                      onClick={() => sendMagicLink({ forceResend: true })}
                      disabled={isSending}
                      variant="ghost"
                      size="sm"
                    >
                      {isSending ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          {_(msg`Sending...`)}
                        </>
                      ) : (
                        _(msg`Resend Magic Link`)
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        // OTP Mode
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="otp"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {_(msg`Verification Code`)}
                </label>
                <Input
                  id="otp"
                  type="text"
                  value={otp}
                  onChange={(e) => handleOtpChange(e.target.value)}
                  placeholder={_(msg`Enter 6-digit code`)}
                  className="text-center text-lg font-mono tracking-wider"
                  maxLength={6}
                  autoComplete="one-time-code"
                />
              </div>

              {error && (
                <ErrorCallout message={error} status={errorStatus}>
                  {remainingAttempts !== null && remainingAttempts > 0 ? (
                    <p className="text-xs opacity-80">
                      {remainingAttempts} {remainingAttempts === 1 ? _(msg`attempt`) : _(msg`attempts`)} {_(msg`remaining`)}
                    </p>
                  ) : null}
                </ErrorCallout>
              )}

              {successMessage && !error && (
                <div className="bg-green-50 border border-green-200 rounded-md p-3">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <p className="text-green-700 text-sm">
                      {successMessage}
                    </p>
                  </div>
                </div>
              )}

              <Button
                onClick={verifyOTP}
                disabled={isVerifying || otp.length !== 6}
                className="w-full bg-[#166B93] hover:bg-[#125a7a]"
              >
                {isVerifying ? _(msg`Verifying...`) : _(msg`Verify & Continue`)}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {authMode === "otp" && (
        <div className="text-center space-y-3">
          {timeLeft > 0 ? (
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                {error && error.includes("already sent") 
                  ? _(msg`You can request a new code in`) 
                  : _(msg`Code expires in`)
                }
              </p>
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-50 rounded-md border border-gray-200">
                <RefreshCw className="w-4 h-4 text-gray-500" />
                <span className="text-lg font-mono font-semibold text-gray-700">
                  {formatTime(timeLeft)}
                </span>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                {error && error.includes("expired") 
                  ? _(msg`Verification code has expired`) 
                  : _(msg`Didn't receive the code?`)
                }
              </p>
              <Button
                onClick={sendOTP}
                disabled={isSending}
                variant="outline"
                size="sm"
                className="min-w-[140px]"
              >
                {isSending ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    {_(msg`Sending...`)}
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    {error?.includes("expired") || error?.includes("Maximum") 
                      ? _(msg`Get New Code`) 
                      : _(msg`Resend Code`)
                    }
                  </>
                )}
              </Button>
            </div>
          )}

          <div className="pt-2">
            <Button
              onClick={() => {
                setAuthMode("magic-link");
                setError("");
                setErrorStatus(null);
                setSuccessMessage("");
              }}
              variant="ghost"
              size="sm"
            >
              {_(msg`← Back to Magic Link`)}
            </Button>
          </div>
        </div>
      )}

      {/* Back Button */}
      {onBack && (
        <div className="text-center">
          <Button onClick={onBack} variant="ghost" size="sm">
            {_(msg`Back to Review`)}
          </Button>
        </div>
      )}
    </div>
  );
}
